import type {
  MatchOperation,
  MatchSource,
  OpFilterMode,
} from '../types/matchEvents.types';
import { parseTsToMillis, type MatchEventRow } from './matchEventsFeed';
import {
  amendedPhrase,
  centsFmt,
  executedPhrase,
  placedPhrase,
  type OrderLegAction,
} from './orderPhrases';
import {
  getNotificationDisplay,
  NOTIFICATION_DISPLAY,
} from '../components/notifications/notification-display';

/**
 * Live-hub operation helpers — pure derivation for the `match_operation`
 * side of the feed (the sniper/manual actions) and the active-match `source`
 * badge. Kept free of React / DOM so the classifier, filter predicate, badge
 * mapping, summary formatter and the incident+operation merge are trivially
 * unit-testable. Mirrors the opener's auto (⚡) / manual (✋) split.
 */

/** Automatic vs manual — the two op lanes (mirrors the opener notifications). */
export type OpKind = 'automatic' | 'manual';

/** Op `type`s emitted by the sniper (automatic lane). */
export const AUTOMATIC_OP_TYPES = new Set<string>([
  'sniper_fired',
  'sniper_auto_no_fired',
  'sniper_var_reversal',
  'sniper_var_rebuy',
  'sniper_goal_cancelled',
  'sniper_fire_blocked',
  'sniper_ladder_sell_placed',
  'sniper_ladder_sell_amended',
  'sniper_ladder_sell_filled',
  'sniper_buy_filled',
]);

/** Op `type`s emitted by an operator action (manual lane). */
export const MANUAL_OP_TYPES = new Set<string>(['manual_order']);

/**
 * Resolve an operation's lane. The wire `kind` is authoritative; when it is
 * missing / unrecognised we fall back to the `type` registry (manual types →
 * manual, everything else → automatic), so a new sniper op type still lands in
 * the automatic lane rather than vanishing.
 */
export function classifyOperation(op: {
  kind?: string | null;
  type: string;
}): OpKind {
  if (op.kind === 'automatic') return 'automatic';
  if (op.kind === 'manual') return 'manual';
  if (MANUAL_OP_TYPES.has(op.type)) return 'manual';
  return 'automatic';
}

/** The Auto | Manual | All op-filter predicate. */
export function matchesOpFilter(
  op: { kind?: string | null; type: string },
  mode: OpFilterMode,
): boolean {
  if (mode === 'all') return true;
  const kind = classifyOperation(op);
  return mode === 'auto' ? kind === 'automatic' : kind === 'manual';
}

/** Visual config for an op row (⚡ automatic = blue, ✋ manual = purple). */
export interface OperationDisplay {
  emoji: string;
  color: string;
  label: string;
  kind: OpKind;
}

export function operationDisplay(op: {
  kind?: string | null;
  type: string;
}): OperationDisplay {
  const kind = classifyOperation(op);
  return kind === 'automatic'
    ? { emoji: '⚡', color: '#42a5f5', label: 'automatic', kind }
    : { emoji: '✋', color: '#ab47bc', label: 'manual', kind };
}

/**
 * The GLYPH for an op row — its TYPE's symbol, not its lane's.
 *
 * The lane (⚡ automatic / ✋ manual) stays on the chip beside it, but as the row's
 * leading mark it made every automatic op look identical: a fire, its fill, a
 * defensive exit and a goal retraction all read as the same bolt, so nothing in the
 * feed stood out from anything else. The type map already assigns each one its own
 * symbol (and keeps ⚡ for the fire itself, which IS the sweep going out).
 *
 * Falls back to the lane glyph for a type with no entry — a manual order, or a
 * backend type this build predates.
 */
export function operationTypeEmoji(op: {
  kind?: string | null;
  type: string;
}): string {
  // The map is consulted DIRECTLY rather than through `getNotificationDisplay`,
  // which resolves an unknown type to a generic placeholder glyph — that would
  // render a type this build predates as a neutral dot instead of falling back to
  // the lane it demonstrably belongs to.
  const entry = (
    NOTIFICATION_DISPLAY as Record<string, { emoji: string } | undefined>
  )[op.type];
  return entry?.emoji ?? operationDisplay(op).emoji;
}

/**
 * The confidence LSports attached to the frame this op is about, as a percent
 * string ("30%"), or null when the op carries none.
 *
 * Only a retraction carries one today, and it is the number that decides how to
 * read the row: a goal pulled at 10% is the feed hedging, one pulled at 100% is the
 * goal being gone. Rounded to whole percent — the wire value is a ramp position
 * (0.30000000000000004), not a measurement worth decimals.
 */
export function formatOpConfidence(op: {
  confidence?: number | null;
}): string | null {
  const c = op.confidence;
  if (c == null || !Number.isFinite(c)) return null;
  return `${Math.round(c * 100)}%`;
}

/** The provenance badge for an active match (⚡ group / ✋ manual / both). */
export interface SourceBadge {
  emoji: string;
  label: string;
  color: string;
}

export function sourceBadge(source: MatchSource): SourceBadge {
  switch (source) {
    case 'group':
      return { emoji: '⚡', label: 'group', color: '#42a5f5' };
    case 'manual':
      return { emoji: '✋', label: 'manual', color: '#ab47bc' };
    case 'both':
      return { emoji: '⚡✋', label: 'both', color: '#7e57c2' };
    default:
      return { emoji: '•', label: String(source), color: '#9e9e9e' };
  }
}

/** Round to at most 4 decimals and drop trailing zeros ("0.8300" → "0.83"). */
function trimNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  return Number(n.toFixed(4)).toString();
}

/** filled/requested size string, or the single side that's present. */
export function formatOperationSize(op: MatchOperation): string {
  // A PLACEMENT-only frame's `filled` is not a fill count — the ladder's
  // `sniper_ladder_sell_placed` reports `held`, the size it just rested. So
  // "100/100" would read as a completed fill under a "placed" chip. Render the
  // resting size alone instead (`sizeRequested` is the original sweep size, not
  // what is on the book, so `filled` is the better reading when both exist).
  if (isPlacementOnlyType(op.type)) {
    const resting = op.filled ?? op.sizeRequested ?? null;
    return resting == null ? '' : trimNumber(resting);
  }
  const { filled, sizeRequested } = op;
  if (filled != null && sizeRequested != null) {
    // Drop the denominator once the order is complete. `filled` can EXCEED
    // `sizeRequested` — a sale-and-rebuy cycle accumulates against the original
    // sweep size — and "120/100" reads as a broken counter rather than a filled
    // order.
    if (filled >= sizeRequested) return trimNumber(filled);
    return `${trimNumber(filled)}/${trimNumber(sizeRequested)}`;
  }
  if (sizeRequested != null) return trimNumber(sizeRequested);
  if (filled != null) return trimNumber(filled);
  return '';
}

/**
 * Op types that REPRICE OR RESIZE an order already working on the book, rather
 * than posting a new one. Kept apart from the plain placements because "amended"
 * on its own never told the operator whether anything traded.
 */
const AMEND_OP_TYPES = new Set<string>(['sniper_ladder_sell_amended']);

/** True when this op moved an existing order instead of posting a fresh one. */
export function isAmendType(type: string): boolean {
  return AMEND_OP_TYPES.has(type);
}

/**
 * The operator-facing name of an op type, from the shared notification display
 * map. The raw wire type (`sniper_ladder_sell_placed`) is an identifier, not
 * language — every other notification surface already renders the label, and
 * the live hub's op rows were the one place still showing the id.
 * Falls back to the raw type for an op with no entry (a manual order, or a
 * backend type this build predates) rather than rendering an empty chip.
 */
export function operationTypeLabel(type: string): string {
  return getNotificationDisplay(type).label ?? type;
}

/**
 * The size an ORDER-PLACEMENT frame is about — what is (now) working on the
 * book. A placement-only type reports that in `filled` (the engine reuses the
 * field for the resting/held size), while a frame that CAN fill reports the
 * order's own size in `sizeRequested`. Distinct from
 * {@link formatOperationSize}, which is the filled/requested progress pair.
 */
function formatRestingSize(op: MatchOperation): string {
  const resting = isPlacementOnlyType(op.type)
    ? op.filled ?? op.sizeRequested
    : op.sizeRequested ?? op.filled;
  return resting == null ? '' : trimNumber(resting);
}

/**
 * True when `reason` only restates something the phrase already says, so
 * appending it is noise. Two cases, both real on the wire:
 *   - `ladder_sell:0.9800` beside a phrase that already renders 98¢ — the same
 *     number twice;
 *   - `ladder:buy_complete` beside a phrase that already reads "Bought 200
 *     shares" with no outstanding remainder.
 * Anything else (`book_unavailable`, `period=full`, `alert_var_defense`) is the
 * only place that information exists and always survives.
 */
function reasonRestatesPhrase(
  reason: string,
  phase: OpLifecyclePhase,
  price: number | null,
): boolean {
  const trailingNumber = /[:=]\s*(-?\d+(?:\.\d+)?)\s*$/.exec(reason);
  if (trailingNumber && price != null && Number(trailingNumber[1]) === price) {
    return true;
  }
  return phase === 'filled' && /(^|[:_])complete$/.test(reason);
}

/**
 * One-line human summary of an operation.
 *
 * ORDER-LIFECYCLE ops lead with a phase-aware phrase from the shared
 * {@link ./orderPhrases} vocabulary — the same language the opener's
 * notifications page speaks — so a PLACEMENT can never be read as an execution:
 *
 *   `kalshi · Over 0.5 goals scored · Placed a sell order: 30 shares at 98¢ (nothing traded)`
 *   `kalshi · Over 0.5 goals scored · Bought 30 of 200 shares at 95.13¢`
 *
 * Before this, both rendered as `venue · market · size · @ price` with no verb
 * and no phase, and a resting cover read exactly like a fill.
 *
 * Everything else (blocked fires, manual orders — ops with no order lifecycle)
 * keeps the neutral `venue · market · SIDE · size · @ price` shape: there is no
 * order phase to name, and inventing one would be worse than none.
 *
 * `reason` is appended after an em dash unless it merely restates the phrase.
 */
export function formatOperationSummary(op: MatchOperation): string {
  const parts: string[] = [];
  if (op.venue) parts.push(op.venue);
  const market = op.marketLabel ?? op.marketId;
  if (market) parts.push(String(market));

  const price = op.avgPrice ?? op.price ?? null;
  const lifecycle = operationLifecycle(op);
  if (lifecycle) {
    parts.push(lifecyclePhrase(op, lifecycle, price));
  } else {
    if (op.side) parts.push(op.side.toUpperCase());
    const size = formatOperationSize(op);
    if (size) parts.push(size);
    if (price != null) parts.push(`@ ${centsFmt(price)}`);
  }

  let summary = parts.join(' · ');
  const reason =
    op.reason && lifecycle && reasonRestatesPhrase(op.reason, lifecycle.phase, price)
      ? null
      : op.reason;
  if (reason) summary = summary ? `${summary} — ${reason}` : reason;
  return summary;
}

/** The verb-bearing clause for one order-lifecycle op. */
function lifecyclePhrase(
  op: MatchOperation,
  lifecycle: OpLifecycle,
  price: number | null,
): string {
  const action: OrderLegAction = lifecycle.leg;
  if (lifecycle.phase === 'placed') {
    const qty = formatRestingSize(op) || undefined;
    return isAmendType(op.type)
      ? amendedPhrase(action, qty, price)
      : placedPhrase(action, qty, price);
  }
  const filled = op.filled ?? undefined;
  return executedPhrase(action, filled, op.sizeRequested ?? undefined, price);
}

/**
 * The order-lifecycle vocabulary the Sniper card renders, mirroring how the
 * opener's notifications page reads a fill: which LEG moved (the BUY entry vs
 * the SELL exit) and how far the order got (placed → partial → filled).
 */
export type OpLifecycleLeg = 'buy' | 'sell';
export type OpLifecyclePhase = 'placed' | 'partial' | 'filled';

export interface OpLifecycle {
  leg: OpLifecycleLeg;
  phase: OpLifecyclePhase;
}

/** Which leg each order-lifecycle op type belongs to. */
const LIFECYCLE_LEG: Record<string, OpLifecycleLeg> = {
  // The FAK sweep IS the entry order; it re-emits as its `filled` climbs.
  sniper_fired: 'buy',
  // The period-close auto-NO tail — a GTC BUY on the No/Under side.
  sniper_auto_no_fired: 'buy',
  // The VAR all-clear buy-back.
  sniper_var_rebuy: 'buy',
  // Every booked BUY fill delta (cumulative `filled` + `sizeRequested`).
  sniper_buy_filled: 'buy',
  // The confidence ladder's resting exit.
  sniper_ladder_sell_placed: 'sell',
  sniper_ladder_sell_amended: 'sell',
  sniper_ladder_sell_filled: 'sell',
  // The VAR-defense exit (break-even / dump-to-floor) is a SELL.
  sniper_var_reversal: 'sell',
};

/**
 * Types whose `filled` is NOT an executed quantity. The engine reuses that field
 * for whatever size the frame is about, so a consumer reading it as "how much
 * executed" invents an execution that never happened:
 *   - `sniper_ladder_sell_placed` / `_amended` → `held`, the size just rested;
 *   - `sniper_var_reversal` → `held` (`alert_reversal`), what we still hold and
 *     have just placed a GTC exit for;
 *   - `sniper_var_rebuy` → the buy-back quantity (`alert_var_defense`).
 * All four also carry `avgPrice: None` and a hardcoded `sizeRequested =
 * cfg.size`, so rendering them as fills produced a fabricated "60 of 100 sold"
 * for what was really "a sell for 60 was just placed". Their REAL executions
 * arrive separately as ladder-sell / buy fills.
 */
const PLACEMENT_ONLY_TYPES = new Set<string>([
  'sniper_ladder_sell_placed',
  'sniper_ladder_sell_amended',
  'sniper_var_reversal',
  'sniper_var_rebuy',
]);

/** True when this op's `filled` is a resting size, not an executed quantity. */
export function isPlacementOnlyType(type: string): boolean {
  return PLACEMENT_ONLY_TYPES.has(type);
}

/**
 * How far an order-lifecycle op got. Partial-vs-complete is DERIVED from the
 * cumulative `filled` against `sizeRequested` — the backend does not split them
 * into separate types — so:
 *   - nothing filled yet (missing / ≤ 0) → `placed`;
 *   - filled short of the requested size → `partial`;
 *   - filled up to (or past, or with no known total) the requested size →
 *     `filled`.
 * Returns null for an op that is not part of the order lifecycle at all.
 */
export function operationLifecycle(
  op: Pick<MatchOperation, 'type' | 'filled' | 'sizeRequested'>,
): OpLifecycle | null {
  const leg = LIFECYCLE_LEG[op.type];
  if (!leg) return null;
  if (PLACEMENT_ONLY_TYPES.has(op.type)) return { leg, phase: 'placed' };
  const filled = op.filled ?? null;
  if (filled == null || filled <= 0) return { leg, phase: 'placed' };
  const total = op.sizeRequested ?? null;
  if (total != null && filled < total) return { leg, phase: 'partial' };
  return { leg, phase: 'filled' };
}

/**
 * A stable de-dupe / React key for an operation (no streamId on the wire).
 * `venue` and `reason` are part of the key so two DISTINCT ops for the same
 * incident/market/side at the same `ts` — e.g. two `sniper_fire_blocked`
 * differing only by venue (Kalshi vs Poly on a null/shared marketId) or by
 * reason — do not collide and get silently dropped by the seen-ops guard.
 */
export function operationKey(op: MatchOperation): string {
  return [
    op.fixtureId,
    op.type,
    op.ts,
    op.incidentKey ?? '',
    op.marketId ?? '',
    op.side ?? '',
    op.venue ?? '',
    op.reason ?? '',
  ].join('|');
}

/**
 * The relay's namespace for every op that COLLAPSES onto one row. Treated as an
 * OPAQUE prefix — deliberately never parsed apart.
 *
 * `resolveOpStreamId` (notifications-websocket) is the single authority on op
 * identity, and it guarantees exactly two things:
 *   - a collapsing op gets a key beginning with `sniper:` — today
 *     `sniper:<orderId>` for the fires, `sniper:buyfill:<orderId>` and
 *     `sniper:sellfill:<orderId>` for the fill series;
 *   - every other op keeps the raw Redis entry id, shaped `<millis>-<seq>`,
 *     which can never begin with `sniper:`.
 * That is the whole contract the frontend needs. Slicing an order id back out
 * of the key would re-hardcode today's namespace list, and the backend is
 * actively re-keying the buy-fill row to be MARK-scoped (a mark's gross is
 * mark-level, and the venue `orderId` changes when the VAR all-clear rebuy
 * re-places the buy) — a shape that does not exist yet. Matching on the prefix
 * and using the key verbatim survives that change.
 */
const SNIPER_ID_PREFIX = 'sniper:';

/**
 * The STABLE identity of an op, or null when it has none.
 *
 * `streamId` is the ONLY identity present on both paths — the live frame is
 * stamped before it is emitted, and the Mongo backfill persists it — whereas
 * `orderId` is live-only (it is not on the schema and not in `toOperationFrame`).
 * So the key must come from `streamId` alone: deriving it from `orderId` made
 * the same order collapse differently live than after a refresh, and produced
 * two rows that never reconciled.
 *
 * Which ops share an identity is the BACKEND's decision, not ours. It keys
 * `sniper_fired` and `sniper_buy_filled` into DIFFERENT namespaces on purpose,
 * so the fill's `$set` cannot flip the placement row's `type` in place; the
 * frontend must not merge them either. Ops with a raw entry id — blocked fires,
 * retractions, the ladder place/amend events, VAR reversals, every manual op —
 * have no stable identity and stay append-only via {@link operationKey}.
 *
 * The relay's key is the ONLY source consulted. An earlier `op.id` override was
 * a second, ungated identity path in a function whose whole thesis is that the
 * relay owns identity — and nothing has ever produced that field.
 */
export function operationStableId(op: { streamId?: string | null }): string | null {
  if (op.streamId?.startsWith(SNIPER_ID_PREFIX)) return op.streamId;
  return null;
}

/** Shallow value-equality over the union of both objects' own keys. */
function sameOpContent(a: MatchOperation, b: MatchOperation): boolean {
  const ra = a as unknown as Record<string, unknown>;
  const rb = b as unknown as Record<string, unknown>;
  const keys = new Set([...Object.keys(ra), ...Object.keys(rb)]);
  for (const k of keys) {
    if (ra[k] !== rb[k]) return false;
  }
  return true;
}

/**
 * The monotonic fill floor an op establishes, or null when it establishes none.
 * A PLACEMENT-only frame carries `filled = held` (a resting size, not an
 * executed quantity), so it must neither be compared against a real fill nor
 * seed a floor — otherwise the ladder's `sell_placed(filled=100)` would swallow
 * every subsequent `sell_filled(filled=40)` as "stale".
 */
function fillFloor(op: MatchOperation): number | null {
  if (isPlacementOnlyType(op.type)) return null;
  return op.filled ?? 0;
}

/**
 * Merge a fill-update onto the existing op for the same stable id. `filled`
 * climbs monotonically as the venue order fills, so a stale/out-of-order frame
 * whose `filled` is strictly LOWER is dropped (the trivial guard); otherwise the
 * incoming frame wins (latest-wins for `filled`/`avgPrice`/`ts`/…). Returns the
 * SAME reference when nothing changed so the caller can skip a state update.
 */
function mergeSniperOp(
  existing: MatchOperation,
  incoming: MatchOperation,
): MatchOperation {
  // A strictly OLDER frame always loses, whatever its fill semantics. This is
  // the guard that still holds once a PLACEMENT is the stored row: a placement
  // reports no fill, so it establishes no floor, and without this a stale
  // out-of-order `sell_filled(40)` arriving after a re-amend could win.
  if (parseTsToMillis(incoming.ts) < parseTsToMillis(existing.ts)) {
    return existing;
  }
  const existingFloor = fillFloor(existing);
  const incomingFloor = fillFloor(incoming);
  // Only compare two genuine fill reports. When either side is a placement its
  // `filled` is a resting size, so there is nothing to compare and the newer
  // frame wins (a re-amend after a partial fill legitimately replaces the row).
  if (
    existingFloor != null &&
    incomingFloor != null &&
    incomingFloor < existingFloor
  ) {
    return existing;
  }
  const merged = { ...existing, ...incoming };
  // The gateway backfills durable ops on EVERY `subscribe_match`, so the exact
  // same frame can arrive twice. Returning `existing` unchanged lets the caller
  // skip the state update (and the re-render) entirely.
  return sameOpContent(merged, existing) ? existing : merged;
}

/**
 * Fold one incoming `match_operation` into the operations list. An op with a
 * stable id ({@link operationStableId}) that already exists is UPDATED IN PLACE
 * (same index → feed order preserved); everything else is appended, capped at
 * `maxRows` (oldest evicted). Pure — the hook owns the seen-key de-dupe of exact
 * re-deliveries; this handles the fill-climb of a re-emitted fire.
 */
export function reconcileOperations(
  prev: MatchOperation[],
  op: MatchOperation,
  maxRows: number,
): MatchOperation[] {
  const id = operationStableId(op);
  if (id) {
    const idx = prev.findIndex((o) => operationStableId(o) === id);
    if (idx !== -1) {
      const merged = mergeSniperOp(prev[idx], op);
      if (merged === prev[idx]) return prev;
      const next = prev.slice();
      next[idx] = merged;
      return next;
    }
  }
  const next = [...prev, op];
  return next.length > maxRows ? next.slice(next.length - maxRows) : next;
}

/**
 * Bound a seen-op-key Set in place: evict the oldest (insertion-ordered) keys
 * until `size <= max`. Deleting visited entries mid-iteration is safe for a Set.
 * Used to cap the hook's unbounded de-dupe memory over a long session.
 */
export function boundSeenOps(seen: Set<string>, max: number): void {
  if (max < 0) max = 0;
  if (seen.size <= max) return;
  let toEvict = seen.size - max;
  for (const k of seen) {
    if (toEvict-- <= 0) break;
    seen.delete(k);
  }
}

/** A synthetic "end of period" marker (shape mirrors matchEventsFeed). */
export interface FeedMarker {
  key: string;
  label: string;
  receivedAt: string;
}

/** One entry in the merged per-match feed (incident | operation | marker). */
export type MatchFeedEntry =
  | { kind: 'incident'; ts: string; key: string; row: MatchEventRow }
  | { kind: 'operation'; ts: string; key: string; op: MatchOperation }
  | { kind: 'marker'; ts: string; key: string; label: string };

/**
 * Merge incident rows, operations and period markers into ONE feed, ordered
 * newest-first by timestamp (`receivedAt` for incidents/markers, `ts` for
 * operations). Purely a merge + sort — filtering happens upstream.
 */
export function mergeMatchFeed(
  rows: MatchEventRow[],
  operations: MatchOperation[],
  markers: FeedMarker[] = [],
): MatchFeedEntry[] {
  const entries: MatchFeedEntry[] = [
    ...rows.map((row) => ({
      kind: 'incident' as const,
      ts: row.receivedAt,
      key: row.frame.streamId || row.collapseKey,
      row,
    })),
    ...operations.map((op) => ({
      kind: 'operation' as const,
      ts: op.ts,
      // A sniper fire's stable id keeps its row identity stable across fill
      // updates (climb 0 → 78) so the row refreshes in place rather than
      // remounting; ops without one fall back to the composite key.
      key: operationStableId(op) ?? operationKey(op),
      op,
    })),
    ...markers.map((m) => ({
      kind: 'marker' as const,
      ts: m.receivedAt,
      key: m.key,
      label: m.label,
    })),
  ];
  entries.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  return entries;
}
